import { useCallback, useEffect, useMemo, useState } from 'react';
import noop from 'lodash-es/noop';
import { TeamChatTypes, TeamChatUtils } from '@frontend/api-team-chat';
import { useTeamChatApi } from '../team-chat-api-providers/use-team-chat-api';

export const useGetUserFromConversation = ({
  api,
  conversationQueryKey,
  enabled,
}: {
  api: 'stream' | 'mock';
  conversationQueryKey: string[];
  enabled: boolean;
}) => {
  const [usersMap, setUsersMap] = useState(new Map<string, TeamChatTypes.User>());

  const { useGetUsersFromConversation } = useTeamChatApi(api);
  const { usersMap: usersMapFromConversation } = useGetUsersFromConversation(conversationQueryKey, enabled);

  useEffect(() => {
    // When a conversations is updated we will need to update the users list with the new users from the conversation,
    // if there are any new ones
    // const newUsersMap = new Map<string, TeamChatTypes.User>(usersMap);

    setUsersMap((prev) => {
      const newUsersMap = new Map<string, TeamChatTypes.User>(prev);
      usersMapFromConversation?.forEach((user) => {
        newUsersMap.set(user.userID, user);
      });

      if (!TeamChatUtils.isUsersMapEqual(prev, newUsersMap)) {
        setUsersMap(newUsersMap);
      }

      return prev;
    });
  }, [usersMapFromConversation]);

  const setUsers = useCallback(
    (
      updater: TeamChatTypes.User[] | ((prev: TeamChatTypes.User[] | undefined) => TeamChatTypes.User[] | undefined)
    ) => {
      const currentUsers = Array.from(usersMap.values());
      const newUsers =
        typeof updater === 'function'
          ? (updater as (prev: TeamChatTypes.User[] | undefined) => TeamChatTypes.User[] | undefined)(
              currentUsers.length ? currentUsers : undefined
            )
          : updater;
      if (newUsers?.length) {
        const newMap = new Map<string, TeamChatTypes.User>(usersMap);
        newUsers.forEach((user) => {
          newMap.set(user.userID, user);
        });

        if (!TeamChatUtils.isUsersMapEqual(usersMap, newMap)) {
          setUsersMap(newMap);
        }
      }
    },
    [usersMap]
  );

  const otherUsers = useMemo(() => {
    return Array.from(usersMap.values());
  }, [usersMap]);

  return {
    otherUsers,
    setUsers,
    _removeUsers: () => setUsersMap(new Map<string, TeamChatTypes.User>()),
    // required props to be returned to satisfy team chat provider interface, these are not relevant for this hook
    isLoadingUsers: false,
    isUsersFetched: true,
    usersError: undefined,
    refetchUsers: noop,
  };
};
